import {
  Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef,
} from "@angular/core";
import { Table } from "primeng/table";
import { PageDetails, TableSchema, TableField } from "../tps/utils/util-interfaces";
import { deepClone, isSameCalendarDate } from "../tps/utils/util-functions";

// Type definitions for component
export type TableType = "trade_blotter" | "Odetails" | string;

export interface RowData {
  [key: string]: any;
  inst_id?: number | string;
  trade_id?: number | string;
  Trade_status?: string;
  fund_id?: number | string;
  order_id?: number | string;
  tradar_code?: string;
  canEdit?: boolean;
  isCheckable?: boolean;
}

export interface FilterEvent {
  value: string | { [key: string]: any };
  field: string;
  matchMode: string;
  fieldType?: string;
}

export interface EditEvent {
  row: RowData;
  index: number;
}

export interface ViewEvent {
  row: RowData;
  index: number;
}

export interface BadgeOption {
  label: string;
  backgroundColor?: string;
  color?: string;
}

export interface HeaderSelectOption {
  label: string;
  value: any;
  [key: string]: any;
}

export interface DateRange {
  [key: string]: Date[] | null;
}

@Component({
  selector: "app-dynamic-table-pagination",
  templateUrl: "./dynamic-table-pagination.component.html",
  styleUrls: ["./dynamic-table-pagination.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicTablePaginationComponent implements OnInit, OnDestroy {
  @Input() tableSchema!: TableSchema;
  @Input() value: RowData[] = [];
  @Input() headerSelectOptions: { [key: string]: HeaderSelectOption[] } = {};
  @Input() badgeOptions: { [key: string]: BadgeOption } = {};
  @Input() tableRows: number = 0;
  @Input() rowsCount: number = 0;
  @Input() pageIndex: number = 0;
  @Input() type: TableType = "";
  @Input() canExport: boolean = false;
  @Input() canHideExportPdf: boolean = false;
  @Input() canDisplay: boolean = true;
  @Input() emptyMessage: string = "No records found.";
  @Input() disableDelete: boolean = false;
  @Input() disableDlt: boolean = false;
  @Input() filterDropdown: HeaderSelectOption[] = [];
  @Input() inlineEdit: boolean = false;
  @Input() selectedRows: any;
  selectedOptions: { [key: string]: any[] } = {};

  @Output() updateInParent = new EventEmitter();
  @Output() editInParent = new EventEmitter<EditEvent>();
  @Output() viewInParent = new EventEmitter<ViewEvent>();
  @Output() deleteFromParent = new EventEmitter<RowData>();
  @Output() Filter = new EventEmitter<FilterEvent>();
  @Output() pageDetails = new EventEmitter<PageDetails>();

  @ViewChild("dt") table!: Table;

  filteredValue: RowData[] = [];
  rows: number = 20;
  // selectedRows: RowData[] = [];
  filters: { [key: string]: string | { [key: string]: any } } = {};
  dateRanges: DateRange = {};
  date: Date | null = null;

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly DEBOUNCE_DELAY = 300; // Reduced from 600ms for better responsiveness

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    if (this.tableSchema && !this.tableSchema.rowsCount) {
      this.tableSchema.rowsCount = 10;
    }
  }

  ngOnDestroy(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["tableSchema"]?.currentValue) {
      const curr = changes["tableSchema"].currentValue as TableSchema;
      if (curr.rowsCount) {
        this.rows = curr.rowsCount;
      }
    }

    if (changes["value"]?.currentValue) {
      const curr = changes["value"].currentValue as RowData[];
      // Only clone if value actually changed to avoid unnecessary deep clones
      if (changes["value"].previousValue !== curr) {
        this.filteredValue = deepClone(curr);
        // Reset filters when data changes
        this.filters = {};
      }
    }
  }

  clicked() {
    console.log("this.value", this.value);
  }

  onFilter(
    searchString: string | { [key: string]: any },
    field: string,
    searchType?: string,
    fieldType?: string
  ) {
    clearTimeout(this.debounceTimer);

    this.debounceTimer = setTimeout(() => {
      let updatedSearchString: string | { [key: string]: any } = "";
      switch (fieldType) {
        case "text":
          updatedSearchString = searchString;
          break;

        case "dropdown":
          if (searchString["label"] === "ALL") {
            updatedSearchString = null;
          } else {
            updatedSearchString = searchString["label"];
          }
          break;
        
        case "date":
          updatedSearchString = searchString;
          break;

        default:
          break;
      }

      this.filters[field] = updatedSearchString;
      this.deleteClearedFilterFields();

      const valueCopy = deepClone(this.value);
      this.filteredValue = valueCopy.filter((row) => {
        for (const [key, filterValue] of Object.entries(this.filters)) {
          // Skip filters that are null, undefined, or empty string
          if (!filterValue) continue;

          const rowValue = (row[key] ?? "").toString().toLowerCase();
          const filterText = filterValue.toString().toLowerCase();

          if (this.dateRanges[key]) {
            if (!isSameCalendarDate(rowValue, filterText)) {
              return false;
            }
          } else {
            // If row does not include filter text, exclude it
            if (!rowValue.includes(filterText)) {
              return false;
            }
          }
        }

        return true;
      });
      this.cdr.markForCheck();
    }, 600);
  }

  deleteClearedFilterFields() {
    for (const key in this.filters) {
      const val = this.filters[key];
      if (
        val === "" ||
        val === null ||
        val === undefined ||
        (Array.isArray(val) && val.length === 0)
      ) {
        delete this.filters[key];
      }
    }
  }

  pageChange(event) {
    const isRowsChanged = this.rowsCount !== event.rows;
    this.pageIndex = isRowsChanged ? 0 : event.first;

    this.rows = event.rows;
    this.pageDetails.emit({
      first: this.pageIndex,
      rows: event.rows,
      isRowsChanged: isRowsChanged,
    });
  }

  onSelectRow() {
    let newList = [];
    if (this.type === "trade_blotter") {
      this.selectedRows.forEach((selectedRow) => {
        this.value.forEach((parentTableRow) => {
          if (selectedRow.inst_id === parentTableRow.inst_id) { newList.push(parentTableRow); }
        });
      });
    } else {
      this.selectedRows.forEach((selectedRow) => {
        this.value.forEach((parentTableRow) => {
          if (selectedRow.fund_id === parentTableRow.fund_id) { newList.push(parentTableRow); }
        });
      });
    }

    this.updateInParent.emit([...newList]);
    if (this.type === "Odetails") {
      localStorage.setItem("dcSelectedRows", JSON.stringify([...newList]));
    } else {
      localStorage.setItem("selectedRows", JSON.stringify([...newList]));
    }
  }

  onRowUnselect(row) {
    let filteredList = [];

    if (this.type === "Odetails") {
      filteredList = this.selectedRows.filter((obj) => row.data.order_id !== obj.order_id);
      this.selectedRows = [...filteredList];
      localStorage.setItem("dcSelectedRows", JSON.stringify(this.selectedRows));
    } else if (this.type === "trade_blotter") {
      filteredList = this.selectedRows.filter((obj) => row.data.inst_id !== obj.inst_id);
      localStorage.setItem("selectedRows", JSON.stringify([...filteredList]));
    } else {
      filteredList = this.selectedRows.filter((obj) => row.data.tradar_code !== obj.tradar_code);
      localStorage.setItem("selectedRows", JSON.stringify([...filteredList]));
    }

    this.updateInParent.emit([...filteredList]);
  }

  selectAllRows() {
    if (this.type === "Odetails") {
      localStorage.setItem("dcSelectedRows", JSON.stringify(this.selectedRows));
    } else {
      localStorage.setItem("selectedRows", JSON.stringify(this.selectedRows));
    }
    this.updateInParent.emit(this.selectedRows);
  }

  onClickEdit(row, index) {
    this.editInParent.emit({ row: row, index: index });
  }

  onClickView(row, index) {
    this.viewInParent.emit({ row: row, index: index });
  }

  onClickDelete(data) {
    this.deleteFromParent.emit(data);
  }

  // Stub methods for template compatibility
  exportExcel(data: any) {}
  exportPdf(columns: any, data: any) {}
  onClickClosetps(field: string) {}
  onClickClose(date: any, field: string) {
    delete this.filters[field];
    this.onFilter(date, field, "contains", "date");
  }
  onSelectDate(date: any, field: string) {
    this.onFilter(date, field, "contains", "date");
  }
  onSelectDateRange(dates: any, field: string, checkingParameter: string) {}
  onClickViewParams(row: any, index: number) {}
  onClickUpdate(data: any) {}
  onClickCancel() {}
  changeInputSwitch(event: any, id: any, rowData: any) {}

  handleResize(event: any) {
    const newWidth = event.delta; // or event.element.style.width depending on PrimeNG version

    const colIndex = event.element.cellIndex;
    if (colIndex) {
      const existingWidth = this.tableSchema.fields[colIndex].width;
      const newWidthValue = parseFloat(existingWidth)  + newWidth + "px";
      this.tableSchema.fields[colIndex].width = newWidthValue;
    }
  }

  trackByColumn(index:number, column:any) {
    return column?.field
  }

  click() {
    console.log("this.tableSchema.fields", this.tableSchema.fields);
  }

  getSubtotal(columnFieldName: string): number {
    // Use filteredValue if you want subtotal of filtered/paged data
    const data = this.filteredValue && this.filteredValue.length ? this.filteredValue : this.value;
    if (!data || data.length === 0) {
      return 0;
    }
    return data.reduce((sum, row) => sum + (parseFloat(row[columnFieldName]) || 0), 0);
  }

  onTableFilter(event: any) {
    // event.filteredValue contains the filtered rows
    this.filteredValue = event.filteredValue || [];
    this.cdr.markForCheck(); // If using OnPush change detection
  }

  getTableScrollHeight(): string {
    const rowCount = this.filteredValue?.length ?? this.value?.length ?? 0;
    const rowHeight = 40;
    const extraHeight = 140;
    const calculatedHeight = rowCount * rowHeight + extraHeight;
    let minHeight = 300;
    let limitHeight = 300;

    if (this.tableSchema && this.tableSchema['scrollHeight']) {
      const scrollHeight = this.tableSchema['scrollHeight'];
      // Handle calc(100vh - XXXpx)
      const calcMatch = /^calc\(\s*100vh\s*-\s*(\d+)px\s*\)$/i.exec(scrollHeight);
      if (calcMatch) {
        const offSetPX = parseInt(calcMatch[1], 10);
        // Calculate based on current viewport height
        limitHeight = Math.min(window.innerHeight - offSetPX, calculatedHeight)
      } else {
        // Try to parse as a number
        const parsed = parseFloat(scrollHeight);
        if (!isNaN(parsed)) {
          limitHeight = parsed;
        }
      }
    }

    const height = Math.max(minHeight, limitHeight);
    return `${height}px`;
  }

  // Handles multiSelect filter logic to ensure 'ALL' (null) shows all results
  onMultiSelectChange(selectedList: any[], field: string, dt: Table) {
    if (selectedList.length === 0) {
      // If no selection, clear the filter
      dt.filter(null, field, 'in');
    } else {
      for (let selectedOption of selectedList) {
        if (selectedOption === null || selectedOption === undefined) {
          // If 'ALL' is selected, clear the filter
          dt.filter(null, field, 'in');
          return;
        }
      }
      // Apply filter with selected options
      dt.filter(selectedList, field, 'in');
    }
  }
}